import {
  formatCoachingScore,
  getCoachingScoreRange,
  type CoachingGradingValueType,
} from "@repo/app-types/entities/coachingScore";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { clamp } from "../helpers/helpers.js";

interface CoachingScoreTargetBarProps {
  /** The rep's current average score, or null when there are no graded calls. */
  currentScore: number | null;
  /** The target average score the rep is aiming for. */
  target: number;
  /** Determines the scale (1-5 vs 0-1 pass rate) and value formatting. */
  gradingValueType: CoachingGradingValueType;
  /** Whether the current score is still being fetched (suppresses the no-calls note). */
  isLoading?: boolean;
}

/**
 * A linear progress bar plotting the rep's current score and target score on
 * the criterion's native scale. The track fills up to the current score, with a
 * node at the current score and a node at the target.
 */
export const CoachingScoreTargetBar: FC<CoachingScoreTargetBarProps> = ({
  currentScore,
  target,
  gradingValueType,
  isLoading = false,
}) => {
  const [min, max] = getCoachingScoreRange(gradingValueType);
  const rangeSize = max - min;

  const normalizeScore = (value: number) => {
    const clamped = clamp(value, min, max);
    return ((clamped - min) / rangeSize) * 100;
  };

  const isAchieved = currentScore !== null && currentScore >= target;
  const currentPercent =
    currentScore !== null ? normalizeScore(currentScore) : 0;
  const targetPercent = normalizeScore(target);

  return (
    <div>
      <ScoreTargetTrack
        currentPercent={currentPercent}
        targetPercent={targetPercent}
        isAchieved={isAchieved}
        hasCurrentScore={currentScore !== null}
      />

      <div className="mt-3 flex items-center justify-between gap-4 text-xs font-semibold">
        {currentScore !== null ? (
          <span className="inline-flex items-center gap-1.5 text-emerald-700">
            <span className="size-2 rounded-full bg-emerald-500 ring-2 ring-emerald-100" />
            You {formatCoachingScore(currentScore, gradingValueType)}
          </span>
        ) : (
          <span className="text-xs font-medium text-muted-foreground">
            {isLoading ? "" : "No calls yet this week"}
          </span>
        )}

        <span
          className={twMerge(
            "inline-flex items-center gap-1.5",
            isAchieved ? "text-emerald-700" : "text-foreground",
          )}
        >
          <span
            className={twMerge(
              "size-2 rounded-full ring-2",
              isAchieved
                ? "bg-emerald-500 ring-emerald-200"
                : "bg-gray-950 ring-gray-200",
            )}
          />
          Target {formatCoachingScore(target, gradingValueType)}
        </span>
      </div>
    </div>
  );
};

const ScoreTargetTrack: FC<{
  currentPercent: number;
  targetPercent: number;
  isAchieved: boolean;
  hasCurrentScore: boolean;
}> = ({ currentPercent, targetPercent, isAchieved, hasCurrentScore }) => (
  <div className="relative h-2.5 rounded-full bg-gray-100">
    {hasCurrentScore ? (
      <div
        className="h-full rounded-full bg-emerald-500"
        style={{ width: `${currentPercent}%` }}
      />
    ) : null}

    {hasCurrentScore ? (
      <span
        aria-hidden
        className="absolute top-0 flex h-full -translate-x-1/2 items-center"
        style={{ left: `${currentPercent}%` }}
      >
        <span className="block size-3 rounded-full border-2 border-white bg-emerald-500 ring-1 ring-emerald-600/30" />
      </span>
    ) : null}

    <span
      aria-hidden
      className="absolute top-0 flex h-full -translate-x-1/2 items-center"
      style={{ left: `${targetPercent}%` }}
    >
      <span
        className={twMerge(
          "block size-3 rounded-full border-2 border-white",
          isAchieved
            ? "bg-emerald-500 ring-4 ring-emerald-200"
            : "bg-gray-950 ring-1 ring-gray-950/20",
        )}
      />
    </span>
  </div>
);
